import { useCallback, useRef, useState } from 'react'

import { useReconciliationEntitled } from '@/modules/account'
import { useProject } from '@/modules/projects'
import { ReconciliationWorkspace } from '@/modules/projects/components/dialogs/ReconciliationWorkspace'
import { COVERAGE_FILL_HEX } from '@/modules/projects/components/StatementCoverage/constants'
import { RECONCILIATION_STATUSES } from '@/modules/projects/constants'
import { useDialogActions } from '@/stores'
import { cn } from '@/utils'
import { AccountMap } from './AccountMap'
import { AddAccountDialog } from './AddAccountDialog'
import type { FilingNote } from './AddAccountDialog/types'
import { maskedNumbersText } from './accounts'
import { CarryGhost } from './CarryGhost'
import { ConfirmEditDialog } from './ConfirmEditDialog'
import { insertAfterOf } from './carry'
import { FoldToggle } from './FoldToggle'
import { isUnfiledPile, statementIdOfSegment } from './fromProject'
import { arrangementLookup } from './lookup'
import { MapToolbar } from './MapToolbar'
import type { MapActions } from './mapView'
import {
  filesOf,
  isOneWholeStatement,
  type NewStatementPlace,
  pagesReadOut,
  pickedUpFrom,
  sentenceCase,
} from './moves'
import { OpenStatement } from './OpenStatement'
import { PageReader } from './PageReader'
import { PagesPane } from './PagesPane'
import { PickedUp } from './PickedUp'
import { PickUpPanel } from './PickUpPanel'
import { formatPeriod } from './period'
import { StatementBar } from './StatementBar'
import type { StatementStripHandle } from './StatementStrip'
import type { StatementView } from './types'
import { useConfirmedEdits } from './useConfirmedEdits'
import { useFactEdits } from './useFactEdits'
import { useMapCoverage } from './useMapCoverage'
import { usePageImages } from './usePageImages'
import { usePageMoves } from './usePageMoves'
import { usePageSelection } from './usePageSelection'
import { usePagesFolded } from './usePagesFolded'
import { useProjectSplit } from './useProjectSplit'
import { useStripBands } from './useStripBands'
import { WorkbenchHeader } from './WorkbenchHeader'

export const Workbench = () => {
  const project = useProject()
  const { files, directory, filesArriving, split, views } = useProjectSplit()
  const isReconciliationEntitled = useReconciliationEntitled()
  const { openDialog } = useDialogActions()
  const edits = useConfirmedEdits()
  const { document: sourceDocument, arrangement } = split

  const [openStatementId, setOpenStatementId] = useState<string | null>(null)
  const [activePage, setActivePage] = useState<number | null>(null)
  const {
    folded: pagesFolded,
    toggleFolded: togglePagesFolded,
    unfold: showPages,
  } = usePagesFolded()
  // The strip is between statements: the column keeps the last one, dimmed, and says so.
  const [stripMoving, setStripMoving] = useState(false)
  const [addingAccount, setAddingAccount] = useState<
    'bar' | 'open_statement' | null
  >(null)
  const pageImages = usePageImages(sourceDocument.pages)
  const mapRef = useRef<HTMLDivElement>(null)
  const strip = useRef<StatementStripHandle>(null)

  const { stripStatements, labelForBand } = useStripBands(split, views)
  const lookup = arrangementLookup(arrangement, views, sourceDocument)
  const { holderByPage, pagesOf, viewOf } = lookup
  // Previous and next follow the strip, so pressing next slides it on by one band. A pile of
  // pages no statement claims is not a statement, so it is not walked through.
  const walk = stripStatements
    .filter((held) => !isUnfiledPile(held.statement.id))
    .map((held) => held.statement.id)
  const filedStatements = views.filter(
    (held) => !isUnfiledPile(held.statement.id),
  )

  // Kept stable: the strip settles again whenever the handler it is given changes.
  const activateStatement = useCallback(
    (statementId: string) => {
      const found = views.find((held) => held.statement.id === statementId)
      if (!found) return
      setOpenStatementId(statementId)
      setActivePage(found.pages[0] ?? null)
    },
    [views],
  )

  const statementsBetween = (fromId: string, toId: string) => {
    const from = walk.indexOf(fromId)
    const to = walk.indexOf(toId)
    if (from === -1 || to === -1) return [toId]
    return walk.slice(Math.min(from, to), Math.max(from, to) + 1)
  }

  // Dropping pages on a month is the same move as clicking it, so the month's own click runs. A
  // band has no click of its own, so it names its statement and the move is proposed from here.
  const dropPages = (target: HTMLElement) => {
    const statementId = target.getAttribute('data-band-drop')
    if (statementId === null) {
      target.click()
      return
    }
    moves.fileUnderStatement(statementId, insertAfterOf(target))
  }
  const {
    selected,
    highlight,
    carryFrom,
    carryGhost,
    pressPages,
    view,
    toggle,
    clear,
  } = usePageSelection({
    scrollElement: () => strip.current?.element() ?? null,
    carryScrollElement: () => mapRef.current,
    pagesIn: (box) => strip.current?.pagesIn(box) ?? [],
    // A band's handle picks up its statement whole, every run of it, wherever they sit in the file.
    pagesOfBand: (statementId) => viewOf(statementId)?.pages ?? [],
    bandsBetween: statementsBetween,
    onFocusPage: setActivePage,
    onDropPages: dropPages,
    onActivateBand: activateStatement,
    onPanStart: () => strip.current?.grip(),
    onPanEnd: () => strip.current?.letGo(),
  })

  const mapCoverage = useMapCoverage({
    split,
    views,
    directory,
    accountOfStatement: lookup.accountOfStatement,
  })

  const openView = viewOf(openStatementId) ?? viewOf(walk[0]) ?? null
  const openId = openView?.statement.id ?? ''
  const openSegment = split.segments.get(openId) ?? null
  const shownPage = activePage ?? openView?.pages[0] ?? null
  const activeDocumentPage =
    shownPage === null ? null : lookup.pageAt(shownPage)
  const activeHolder =
    shownPage === null ? undefined : holderByPage.get(shownPage)
  const selectedPages = [...selected].sort((one, other) => one - other)
  const pickedUp = pagesOf(selectedPages)
  const pickedUpSources =
    selectedPages.length === 0
      ? []
      : pickedUpFrom(arrangement.accounts, holderByPage, pickedUp)
  // One statement's figures cannot describe a pick-up that crosses several, so the column shows
  // the pick-up instead of the statement it is resting on.
  const showsPickUp = pickedUpSources.length > 1

  const moves = usePageMoves({
    sourceDocument,
    arrangement,
    lookup,
    selectedPages,
    pickedUp,
    edits,
  })
  const facts = useFactEdits({
    accounts: arrangement.accounts,
    directory,
    openId,
    edits,
    mapRef,
  })

  const stepTo = (offset: number) => {
    const at = walk.indexOf(openId)
    const next = viewOf(at === -1 ? undefined : walk[at + offset])
    return next ? () => activateStatement(next.statement.id) : null
  }

  const filingNote = (): FilingNote | null => {
    if (addingAccount !== 'open_statement' || !openView) return null
    const pages = pagesOf(openView.pages)
    return {
      period: formatPeriod(openView.statement.period),
      pages: sentenceCase(pagesReadOut(pages)),
      fileName: filesOf(pages).join(', '),
    }
  }

  const openReconciliation = () => {
    const fileId = split.fileOf.get(openId)
    if (!openSegment || !fileId) return
    openDialog(ReconciliationWorkspace.id, {
      statementId: fileId,
      segmentId: openSegment.id,
    })
  }

  const mapActions: MapActions = {
    onOpen: (held: StatementView) => activateStatement(held.statement.id),
    onEditRange: (held: StatementView) => {
      activateStatement(held.statement.id)
      showPages()
    },
    onDelete: moves.deleteStatement,
    onOpenSegment: (segmentId: number) =>
      activateStatement(statementIdOfSegment(segmentId)),
    onPutIn: moves.putAtEnd,
    // A statement starts from pages, so an empty month with none in hand shows them.
    onPutInNew: (place: NewStatementPlace) =>
      selectedPages.length === 0 ? showPages() : moves.putInNew(place),
  }

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden bg-background',
        // Every cursor on the screen, or the closed hand flickers back to a pointer the moment
        // the pages are held over a month.
        carryFrom && 'cursor-grabbing! [&_*]:cursor-grabbing!',
      )}
    >
      {/* Mounted regardless of what the map shows: a websocket update must not tear the workspace
          out from under an in-progress edit. */}
      <ReconciliationWorkspace />

      <WorkbenchHeader
        project={project}
        filesArriving={filesArriving}
        counts={{
          accounts: arrangement.accounts.length,
          statements: filedStatements.length,
          pages: sourceDocument.pages.length,
          files: files.length,
        }}
        toolbar={<MapToolbar {...mapCoverage.toolbar} />}
        onUndo={edits.undo}
        onAddAccount={() => setAddingAccount('bar')}
      />

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="relative flex min-h-0 flex-1 flex-col">
            {/* One place in both states: the seam between the map and the pages. */}
            <FoldToggle
              folded={pagesFolded}
              onToggle={togglePagesFolded}
              className="absolute right-6 bottom-4 z-10 border bg-white shadow-sm"
            />
            <AccountMap
              map={mapCoverage.shownMap}
              pagesOf={pagesOf}
              choosing={selectedPages.length > 0}
              openStatementId={openView?.statement.id ?? null}
              followStatementId={openStatementId}
              filters={mapCoverage.filters}
              statusFor={mapCoverage.statusFor}
              scrollRef={mapRef}
              loading={filesArriving}
              highlight={mapCoverage.highlight}
              laneActions={facts.laneActions}
              actions={mapActions}
            />
          </div>

          <div
            className={cn(
              'flex min-h-0 shrink-0 flex-col',
              !pagesFolded && 'max-h-[55%] border-t',
            )}
          >
            <PagesPane
              tintFor={(statementId) =>
                COVERAGE_FILL_HEX[mapCoverage.statusFor(statementId, null)]
              }
              accountOf={(statementId) => {
                const account = lookup.accountOfStatement(statementId)
                return account
                  ? { id: account.id, label: maskedNumbersText(account) }
                  : null
              }}
              holdStill={carryFrom !== null}
              folded={pagesFolded}
              loading={filesArriving}
              statements={stripStatements}
              labelFor={labelForBand}
              selected={selected}
              highlight={highlight}
              activePage={shownPage}
              activeStatementId={openView?.statement.id ?? null}
              ref={strip}
              isLoaded={pageImages.isLoaded}
              srcFor={pageImages.srcFor}
              onPressPages={pressPages}
              onActivatePage={(page, additive) =>
                additive ? toggle(page) : view(page)
              }
              onActivate={activateStatement}
              onPagesWanted={pageImages.request}
              onMotion={setStripMoving}
            />
          </div>
        </div>

        <aside className="flex w-[21rem] shrink-0 flex-col border-l bg-white">
          <div className="relative min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300">
            {showsPickUp ? (
              <PickedUp pages={pickedUp} sources={pickedUpSources} />
            ) : (
              <OpenStatement
                position={walk.indexOf(openId) + 1}
                total={walk.length}
                filesArriving={filesArriving}
                stripMoving={stripMoving}
                onPrevious={stepTo(-1)}
                onNext={stepTo(1)}
              >
                <StatementBar
                  view={openView}
                  pages={pagesOf(openView?.pages ?? [])}
                  accounts={arrangement.accounts}
                  segment={openSegment}
                  isFixable={
                    isReconciliationEntitled &&
                    openSegment?.reconciliationStatus ===
                      RECONCILIATION_STATUSES.needsReview
                  }
                  onSetAccount={facts.fileUnderAccount}
                  onAddAccount={() => setAddingAccount('open_statement')}
                  onSetPeriod={facts.setPeriod}
                  onReview={openReconciliation}
                />
              </OpenStatement>
            )}

            <PageReader
              page={activeDocumentPage}
              statementPages={pagesOf(activeHolder?.pages ?? [])}
              filedUnder={activeHolder ? lookup.nameOf(activeHolder) : null}
              stripMoving={stripMoving}
            />
          </div>

          {selectedPages.length > 0 ? (
            <PickUpPanel
              selectedPages={pickedUp}
              alreadyOneStatement={isOneWholeStatement(holderByPage, pickedUp)}
              onMakeNewStatement={moves.makeNewStatement}
              onTakeOut={moves.takeOut}
              onClear={clear}
            />
          ) : null}
        </aside>
      </div>

      <AddAccountDialog
        open={addingAccount !== null}
        filing={filingNote()}
        onAdd={
          addingAccount === 'open_statement'
            ? facts.addAccountAndFileHere
            : facts.addAccount
        }
        onClose={() => setAddingAccount(null)}
      />

      <ConfirmEditDialog
        pending={edits.pending}
        saving={edits.saving}
        showsVerdict={isReconciliationEntitled}
        onConfirm={async () => {
          if (await edits.confirm()) clear()
        }}
        onCancel={edits.cancel}
      />

      {carryFrom ? (
        <CarryGhost
          from={carryFrom}
          pages={pickedUp}
          srcFor={pageImages.srcFor}
          ghostRef={carryGhost}
        />
      ) : null}
    </div>
  )
}
